// Admin: connect the WhatsApp Business Cloud API (Meta) so wedding invitations go out on
// WhatsApp automatically. Without it, families send them from their own WhatsApp with
// pre-written click-to-chat messages.
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card, CardContent, Typography, Grid, TextField, Button, Switch, FormControlLabel, Alert, Stack, Box, Chip, Link, Divider, MenuItem,
  Autocomplete,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { WHATSAPP_SETTINGS_QUERY, SAVE_WHATSAPP_SETTINGS_MUTATION, TEST_WHATSAPP_MUTATION, MESSAGE_TEMPLATES_QUERY } from '../graphql/queries';

// The test sends the invitation template above, or any approved template synced from Meta
const INVITATION = 'invitation';

export default function WhatsAppSettingsCard() {
  const { data, refetch } = useQuery(WHATSAPP_SETTINGS_QUERY);
  const s = data?.whatsappSettings;
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testTemplate, setTestTemplate] = useState(INVITATION);
  // WhatsApp templates synced from Meta (Admin > Messaging Templates), to choose from
  const { data: tplData } = useQuery(MESSAGE_TEMPLATES_QUERY, { variables: { channel: 'whatsapp' }, fetchPolicy: 'cache-and-network' });
  const waTemplates = (tplData?.messageTemplates || []).filter(t => t.status !== 'deleted');
  const approved = waTemplates.filter(t => t.sendable);
  useEffect(() => {
    if (s && !form) {
      setForm({
        phoneNumberId: s.phoneNumberId, businessNumber: s.businessNumber, accessToken: '', apiVersion: s.apiVersion,
        templateName: s.templateName, templateLanguage: s.templateLanguage, isActive: s.isActive,
      });
    }
  }, [s, form]);
  const [save, { loading: saving }] = useMutation(SAVE_WHATSAPP_SETTINGS_MUTATION, {
    onCompleted: () => { setMsg({ severity: 'success', text: 'WhatsApp settings saved.' }); setForm(f => ({ ...f, accessToken: '' })); refetch(); },
    onError: (e) => setMsg({ severity: 'error', text: e.message }),
  });
  const [test, { loading: testing }] = useMutation(TEST_WHATSAPP_MUTATION, {
    onCompleted: (d) => setMsg({ severity: 'success', text: `Test message: ${d.testWhatsApp}` }),
    onError: (e) => setMsg({ severity: 'error', text: `Test failed: ${e.message}` }),
  });
  if (!s || !form) return null;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const guide = s.templateGuide || {};

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography variant="h6" fontWeight={600}>WhatsApp invitations</Typography>
          <Chip size="small" color={s.isActive && s.configured ? 'success' : 'default'}
            label={s.isActive && s.configured ? 'Automatic sending on' : 'Families send from their own WhatsApp'} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Without this, families send WhatsApp invitations from their own phone — each guest has a button that opens WhatsApp
          with the message already written. Connect the <b>WhatsApp Business Cloud API</b> (Meta) to send them automatically from your business number.
          Providers such as Twilio, Gupshup, Interakt, AiSensy or WATI resell the same API; use Meta's details here.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>One-time setup in Meta</Typography>
          <Typography variant="body2" component="div">
            1. In <Link href="https://business.facebook.com" target="_blank" rel="noopener noreferrer">Meta Business Suite</Link>, create a WhatsApp Business account and add a phone number
            (one not already used on WhatsApp).<br />
            2. In WhatsApp Manager → Message templates, create a template named <b>{guide.name}</b> (category {guide.category}, language English) with this body:
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff', p: 1, borderRadius: 1, my: 0.5, fontSize: 12 }}>{guide.body}</Box>
            The app fills the variables as: {(guide.variables || []).map((v, i) => `{{${i + 1}}} ${v}`).join(', ')}. Wait for Meta to approve it.<br />
            3. In the app's WhatsApp → API setup, copy the <b>Phone number ID</b> and create a <b>permanent access token</b> (System user with whatsapp_business_messaging).<br />
            4. Enter them below, save, send a test, then turn automatic sending on. Meta charges per message; guests should have agreed to receive messages from you.
          </Typography>
        </Alert>

        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField label="Phone number ID" value={form.phoneNumberId} onChange={set('phoneNumberId')} fullWidth /></Grid>
          <Grid item xs={12} md={6}><TextField label="Business WhatsApp number (shown to families)" value={form.businessNumber} onChange={set('businessNumber')} fullWidth placeholder="+91 98400 00000" /></Grid>
          <Grid item xs={12}>
            <TextField label="Access token" type="password" value={form.accessToken} onChange={set('accessToken')} fullWidth
              helperText={s.hasAccessToken ? 'A token is saved — leave empty to keep it' : 'Permanent token from Meta'} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              freeSolo
              options={waTemplates}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2">{o.name} · {o.language}</Typography>
                    <Typography variant="caption" color={o.sendable ? 'success.main' : 'text.secondary'}>{o.sendable ? 'Approved' : o.status}</Typography>
                  </Box>
                </li>
              )}
              inputValue={form.templateName || ''}
              onInputChange={(_, v, reason) => { if (reason !== 'reset') setForm(f => ({ ...f, templateName: v })); }}
              onChange={(_, v) => { if (v && typeof v !== 'string') setForm(f => ({ ...f, templateName: v.name, templateLanguage: v.language })); }}
              renderInput={(params) => <TextField {...params} label="Template name" helperText={waTemplates.length ? 'Pick a template synced from Meta, or type its name' : 'Sync templates in Messaging Templates to pick one'} />}
            />
          </Grid>
          <Grid item xs={6} sm={4}><TextField label="Template language" value={form.templateLanguage} onChange={set('templateLanguage')} fullWidth helperText="e.g. en, en_US, ta" /></Grid>
          <Grid item xs={6} sm={4}><TextField label="API version" value={form.apiVersion} onChange={set('apiVersion')} fullWidth /></Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Send WhatsApp invitations automatically" />
          </Grid>
        </Grid>
        <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} sx={{ mt: 1 }} onClick={() => save({ variables: { input: form } })}>
          {saving ? 'Saving…' : 'Save WhatsApp settings'}
        </Button>

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle2" fontWeight={600} mb={1}>Send a test message</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField select size="small" label="Template" value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)} sx={{ minWidth: 260 }}
            helperText={approved.length ? 'Sent with sample values' : 'Approved templates from Meta appear here after a sync'}>
            <MenuItem value={INVITATION}>Invitation template ({s.templateName || 'not set'})</MenuItem>
            {approved.map(t => <MenuItem key={t.id} value={t.id}>{t.name} · {t.language}</MenuItem>)}
          </TextField>
          <TextField size="small" label="WhatsApp number" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+91 98400 12345" sx={{ minWidth: 220 }} />
          <Button variant="outlined" startIcon={<SendIcon />} disabled={testing || !testTo.trim() || !s.configured} sx={{ alignSelf: 'flex-start', mt: { sm: 0.25 } }}
            onClick={() => test({ variables: { to: testTo, templateId: testTemplate === INVITATION ? null : testTemplate } })}>
            {testing ? 'Sending…' : 'Send test'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
